import { timeout } from 'promise-timeout';
import { BaseActionTrigger } from './types';
import { getComponentInstance } from '../instanceMap';
import { generateComponentActionParams, generateHotareaActionParams, getComponentData } from '../actionParams';

const DEFAULT_MAX_TIMEOUT = 10 * 1000;

export function pipeActions(actions, component, hotArea, payload) {
  return async (originalEvent, { meta, global }) => {
    for (const iAction of actions) {
      const {
        action: { action: actionFn, maxTimeout = DEFAULT_MAX_TIMEOUT },
        target,
      } = iAction;
      const params = hotArea
        ? generateHotareaActionParams(iAction, hotArea, component, global, meta)
        : generateComponentActionParams(iAction, component, global, meta);

      params.payload = payload;
      if (originalEvent) {
        params.originalEvent = originalEvent;
      }

      if (typeof target === 'number') {
        params.target = getComponentData(getComponentInstance(target));
      }

      const fn = typeof actionFn === 'object' ? actionFn.action : actionFn;
      try {
        if (maxTimeout === 'Infinity') {
          await fn(params);
        } else {
          const p = fn(params);
          if (p instanceof Promise) {
            await timeout(fn(params), maxTimeout);
          }
        }
      } catch (e) {
        console.error('Action throw error: ', e);
      }
    }
  };
}

export function generateHandler(actions, trigger, component, hotArea) {
  if (!actions || !actions.length) {
    return undefined;
  }

  const iActions = actions.filter(a => a.trigger === trigger);
  if (!iActions.length) {
    return undefined;
  }

  return pipeActions(iActions, component, hotArea);
}

export function generateHandlers(actions, component, hotArea) {
  const handlers = {};

  const onInit = generateHandler(actions, BaseActionTrigger.INIT, component, hotArea);
  if (onInit) {
    handlers.onInit = onInit;
  }

  const onClick = generateHandler(actions, BaseActionTrigger.CLICK, component, hotArea);
  if (onClick) {
    handlers.onClick = onClick;
  }

  const onMouseEnter = generateHandler(actions, BaseActionTrigger.MOUSE_ENTER, component, hotArea);
  if (onMouseEnter) {
    handlers.onMouseEnter = onMouseEnter;
  }

  const onMouseLeave = generateHandler(actions, BaseActionTrigger.MOUSE_LEAVE, component, hotArea);
  if (onMouseLeave) {
    handlers.onMouseLeave = onMouseLeave;
  }

  const onDoubleClick = generateHandler(actions, BaseActionTrigger.DOUBLE_CLICK, component, hotArea);
  if (onDoubleClick) {
    handlers.onDoubleClick = onDoubleClick;
  }

  const onLongPress = generateHandler(actions, BaseActionTrigger.LONG_PRESS, component, hotArea);
  if (onLongPress) {
    handlers.onLongPress = onLongPress;
  }

  const onEnterView = generateHandler(actions, BaseActionTrigger.ENTER_VIEW, component, hotArea);
  if (onEnterView) {
    handlers.onEnterView = onEnterView;
  }

  const onLeaveView = generateHandler(actions, BaseActionTrigger.LEAVE_VIEW, component, hotArea);
  if (onLeaveView) {
    handlers.onLeaveView = onLeaveView;
  }

  return handlers;
}
